import { isNative } from "@/lib/capacitor";
import { supabase } from "@/integrations/supabase/client";

let registeredForUserId: string | null = null;
let tapListenerAttached = false;

/**
 * Map a push notification's `data.kind` payload to an in-app route.
 * Server-side senders (e.g. supabase/functions/nudge-sold-status) attach
 * `{ kind: "sold_status_nudge", scan_id: "<uuid>" }` so the tap lands
 * the user on the relevant listing-edit sheet instead of /home.
 */
function routeForPushData(data: Record<string, unknown> | null | undefined): string | null {
  if (!data) return null;
  const kind = typeof data.kind === "string" ? data.kind : null;
  const scanId = typeof data.scan_id === "string" ? data.scan_id : null;
  switch (kind) {
    case "sold_status_nudge":
      return scanId ? `/listing/${scanId}/edit` : null;
    default:
      return null;
  }
}

/**
 * Navigate without a full page reload. Mirrors the replaceRoute pattern in
 * nativeDeepLinks — pushState + popstate so React Router picks up the change.
 */
function navigateInApp(path: string): void {
  if (!path) return;
  if (path === window.location.pathname + window.location.search) return;
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export async function registerPush(userId: string): Promise<void> {
  if (!isNative) return;
  if (registeredForUserId === userId) return;

  const { PushNotifications } = await import("@capacitor/push-notifications");

  const perm = await PushNotifications.checkPermissions();
  let status = perm.receive;
  if (status === "prompt" || status === "prompt-with-rationale") {
    const req = await PushNotifications.requestPermissions();
    status = req.receive;
  }
  if (status !== "granted") return;

  // Attach listeners BEFORE register() — registration callback is async
  // and would race a late-attached listener.
  await PushNotifications.addListener("registration", async (token) => {
    await supabase.from("push_tokens").upsert(
      { user_id: userId, token: token.value, platform: "ios" },
      { onConflict: "token" }
    );
  });
  await PushNotifications.addListener("registrationError", (err) => {
    console.warn("Push registration error", err);
  });

  // Notification-tap listener. Idempotent: only attached once per app
  // session (independent of how many times registerPush() is called for
  // different user ids — re-attaching would fire the handler multiple
  // times on a single tap).
  if (!tapListenerAttached) {
    tapListenerAttached = true;
    await PushNotifications.addListener(
      "pushNotificationActionPerformed",
      (action) => {
        const data = (action.notification?.data ?? null) as Record<string, unknown> | null;
        const route = routeForPushData(data);
        if (route) navigateInApp(route);
      },
    );
  }

  await PushNotifications.register();
  registeredForUserId = userId;
}

export function resetPushRegistration(): void {
  registeredForUserId = null;
}
